import { toHTML } from "@portabletext/to-html";
import {
  RecipeMenuProps,
  Recipe as RecipeComponentProps,
} from "@bluebottlecoffee/design-system/components";
import {
  RecipeMenu as ModuleSchema,
  Recipe as RecipeSchema,
} from "../sanity-schema";
import { ImageType, toImageProps, toImageResponsiveProps } from "./image";
import { toBulletedKeyValueListProps } from "./bulleted-key-value-list";
import {
  toCopyObject,
  TranslationGroupSanityType,
} from "../sanity/translation-group-queries";
import { ConversionCopy } from "../../pages/[region]/[lang]/product/[slug]";
import { ConversionSchema, toProductProps } from "./conversion";
import { BrewGuideRecipeCartProps } from "./detail-page-overview";

export type DereferencedRecipeMenu = Omit<ModuleSchema, "recipes"> & {
  recipes: RecipeSchema[];
  copy: TranslationGroupSanityType;
};

type RecipeTransformerProps = {
  data: RecipeSchema;
  lang: string;
  region: string;
  copy: ConversionCopy;
  useCartProps: BrewGuideRecipeCartProps;
};

export function toRecipeProps({
  data,
  lang,
  region,
  copy,
  useCartProps,
}: RecipeTransformerProps): RecipeComponentProps {
  const thumbnailImage: ImageType = {
    _type: "imageWithAltText",
    source: data.menuThumbnail,
    altText: data.name[lang],
  };
  const { equipment } = data;

  const products = equipment.products.map((product) =>
    toProductProps(product as unknown as ConversionSchema, lang),
  );

  return {
    name: data.name[lang],
    menuThumbnail: {
      src: toImageProps(thumbnailImage, lang).src,
    },
    equipment: equipment && {
      title: equipment.title[lang],
      products: products.filter((p) => p.variants.length),
      extraProducts: equipment.extraProducts,
      equipmentCard: {
        addToCartProps: {
          addToCart: copy.addToCart,
          errorMessage: copy.errorGeneric,
          loadingMessage: copy.adding,
          soldOut: copy.soldOut,
        },
        ...useCartProps,
      },
    },
    ...(data.media[0]._type === "imageResponsive"
      ? { image: toImageResponsiveProps(data.media[0], lang) }
      : { video: data.media[0].vimeoUrl }),
    instructions: {
      keyValueList: toBulletedKeyValueListProps(
        data.instructions.bulletedKeyValueList,
        "transparent",
        region,
        lang,
      ),
      ...(data.instructions.list && {
        list: {
          listItems: data.instructions.list.map((item) => ({
            text: item[lang],
          })),
        },
      }),
    },
  };
}

type RecipeMenuTransformerProps = {
  data: DereferencedRecipeMenu;
  lang: string;
  region: string;
  useCartProps: BrewGuideRecipeCartProps;
};

export function toRecipeMenuProps({
  data,
  lang,
  region,
  useCartProps,
}: RecipeMenuTransformerProps): RecipeMenuProps {
  return {
    heading: toHTML(data.heading[lang]),
    recipes: data.recipes.map((recipe) =>
      toRecipeProps({
        data: recipe,
        region,
        lang,
        copy: toCopyObject<ConversionCopy>(lang, data.copy),
        useCartProps,
      }),
    ),
    ...(data.bgColor && { bgColor: data.bgColor }),
  };
}
